import { useEffect, useRef, useState, useCallback } from 'react';
import { realtimeService } from '@/services/realtimeService';
import { authService } from '@/services/auth';

// Subscribes to the org's appointment channel. `onStatusChange` fires ONLY for
// status_change events (backward compatible — Home.jsx relies on this).
// Lifecycle events (created/updated/deleted, published by the appointment
// write endpoints) and reconnect notifications are wired via options.
export function useAppointmentRealtime(
  organizationId,
  onStatusChange,
  { enabled = true, persistent = false, onCreated, onUpdated, onDeleted, onOverrideEvent, onReconnect } = {}
) {
  const callbackRef = useRef(onStatusChange);
  callbackRef.current = onStatusChange;
  const createdRef = useRef(onCreated);
  createdRef.current = onCreated;
  const updatedRef = useRef(onUpdated);
  updatedRef.current = onUpdated;
  const deletedRef = useRef(onDeleted);
  deletedRef.current = onDeleted;
  const overrideRef = useRef(onOverrideEvent);
  overrideRef.current = onOverrideEvent;
  const reconnectRef = useRef(onReconnect);
  reconnectRef.current = onReconnect;

  const [recentlyUpdated, setRecentlyUpdated] = useState(new Set());
  const timersRef = useRef(new Map());

  const markUpdated = useCallback((appointmentId) => {
    setRecentlyUpdated(prev => new Set(prev).add(appointmentId));

    if (timersRef.current.has(appointmentId)) {
      clearTimeout(timersRef.current.get(appointmentId));
    }
    timersRef.current.set(appointmentId, setTimeout(() => {
      setRecentlyUpdated(prev => {
        const next = new Set(prev);
        next.delete(appointmentId);
        return next;
      });
      timersRef.current.delete(appointmentId);
    }, 3000));
  }, []);

  useEffect(() => {
    if (!organizationId || !enabled) return;

    const token = authService.getToken();
    if (token) realtimeService.setAuthToken(token);

    const channelName = `appointment/${organizationId}`;

    const handler = (data) => {
      switch (data?.type) {
        case 'status_change':
          markUpdated(data.appointment_id);
          callbackRef.current?.(data);
          break;
        case 'created':
          markUpdated(data.appointment_id);
          createdRef.current?.(data);
          break;
        case 'updated':
          markUpdated(data.appointment_id);
          updatedRef.current?.(data);
          break;
        case 'deleted':
          deletedRef.current?.(data);
          break;
        // Schedule-override lifecycle (blocks/notes/availability exceptions) —
        // no markUpdated: the green pulse is appointment-specific
        case 'override_created':
        case 'override_updated':
        case 'override_deleted':
          overrideRef.current?.(data);
          break;
        default:
          break;
      }
    };

    realtimeService.subscribe(channelName, handler, { persistent });
    const removeReconnect = realtimeService.onReconnect(() => {
      reconnectRef.current?.();
    });

    const timers = timersRef.current;
    return () => {
      realtimeService.unsubscribe(channelName, handler);
      removeReconnect();
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
    };
  }, [organizationId, enabled, persistent, markUpdated]);

  return { recentlyUpdated };
}
